import { useCallback, useEffect, useMemo, useState } from 'preact/hooks'
import { SearchspringRecommendProvider } from 'libs/searchspring/fetchers/recommend'
import { CarouselResults } from 'libs/searchspring/components/recommendations/CarouselResults'
import clsx from 'clsx'
import { BeaconAnalyticsProvider } from 'libs/searchspring/hooks/useBeaconAnalytics'
import { parseJSON } from '@/components/helpers/utils'
import { useSearchspringContext } from 'libs/searchspring/fetchers/provider'

// Helper component to track results state
const CarouselResultsWrapper = ({ profile, onResultsChange, children }) => {
  const { results, isLoading } = useSearchspringContext()

  // Notify parent when results/loading state changes
  useEffect(() => {
    onResultsChange(profile, {
      hasResults: results?.results?.length > 0,
      isLoading
    })
  }, [results, isLoading, profile, onResultsChange])

  return <>{children}</>
}

export function CarouselTabbed({
  values,
  itemClass,
  productId,
  carouselSettings = {},
  productCardSettings,
  titleContent,
  ctaContent,
  limit
}) {
  const profiles = useMemo(() => (values ? parseJSON(values) : []), [values])
  const parsedSettings = parseJSON(carouselSettings)
  // Handle groupCells value conversion
  if (parsedSettings?.groupCells !== undefined) {
    const value = parsedSettings.groupCells

    // Convert string 'true'/'false' to boolean
    if (value === 'true') {
      parsedSettings.groupCells = true
    } else if (value === 'false') {
      parsedSettings.groupCells = false
      // Convert numeric string to number
    } else if (!isNaN(value) && typeof value === 'string') {
      parsedSettings.groupCells = Number(value)
    }
  }

  const parsedProductCardSettings = parseJSON(productCardSettings)
  const cardSettings =
    productCardSettings && Object.keys(parsedProductCardSettings).length > 0 ? parsedProductCardSettings : {}

  const [activeProfile, setActiveProfile] = useState(profiles[0]?.profile)
  const [profileStates, setProfileStates] = useState({})

  // Callback to update profile states
  const handleResultsChange = useCallback((profile, state) => {
    setProfileStates((prev) => ({
      ...prev,
      [profile]: state
    }))
  }, [])

  // Filter out profiles with no results
  const visibleProfiles = profiles?.filter((profile) => {
    const state = profileStates[profile.profile]
    return !state || state.isLoading || state.hasResults
  })

  useEffect(() => {
    if (visibleProfiles.length > 0) {
      const isActiveProfileVisible = visibleProfiles.some((profile) => profile.profile === activeProfile)
      if (!isActiveProfileVisible) {
        setActiveProfile(visibleProfiles[0].profile)
      }
    }
  }, [visibleProfiles, activeProfile])

  return (
    <div className="grid-main gap-x-1.5 gap-y-1.5">
      <div className="col-[1/-1] flex flex-col gap-y-1.25 self-end md:col-[1/-5] md:flex-row md:items-end">
        <div
          className="sticky left-0 flex items-end pr-1.5 empty:hidden"
          dangerouslySetInnerHTML={{ __html: titleContent }}
        />
        <div className="flex items-end gap-x-1.5 overflow-x-scroll overscroll-none whitespace-nowrap no-scrollbar md-max:container-full">
          {visibleProfiles.map((item) => (
            <button
              className={clsx(
                'h3 w-fit',
                item.profile === activeProfile ? 'active text-line text-line-space' : 'text-neutral-secondary'
              )}
              key={item.profile}
              onClick={() => setActiveProfile(item.profile)}
              aria-label={item.label}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
      <div
        className="col-span-full flex justify-end empty:hidden md:col-[-1/-5] md-max:row-[3] [&_.btn]:md-max:!w-full"
        dangerouslySetInnerHTML={{ __html: ctaContent }}
      />
      <div className="relative col-span-full grid grid-cols-1 grid-rows-1 container-full">
        {profiles.map((item) => (
          <SearchspringRecommendProvider key={item.profile} profile={item.profile} productId={productId} limit={limit}>
            <BeaconAnalyticsProvider profile={item.profile}>
              <CarouselResultsWrapper profile={item.profile} onResultsChange={handleResultsChange}>
                <CarouselResults
                  className={itemClass}
                  data-dl-section-id={item.profile}
                  options={parsedSettings}
                  wrapperClassName={clsx(
                    'transition-opacity duration-150 row-start-1 col-start-1',
                    item.profile !== activeProfile
                      ? 'pointer-events-none touch-none opacity-0 delay-150'
                      : 'duration-300 z-1 opacity-100'
                  )}
                  productCardSettings={cardSettings}
                />
              </CarouselResultsWrapper>
            </BeaconAnalyticsProvider>
          </SearchspringRecommendProvider>
        ))}
      </div>
    </div>
  )
}
